import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getLatestArticles } from '@/mocks/articles';
import { usePageSEO } from '@/hooks/usePageSEO';
import { getWebPageSchema, getBreadcrumbSchema, siteDomain } from '@/utils/schema';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const latestArticles = getLatestArticles(12);

export default function CategoryPage() {
  usePageSEO({
    title: '全部分类 - kaiyuntiyu | 体育资讯分类导航',
    description: '浏览kaiyuntiyu全部体育分类，包括足球、篮球、网球、电竞和综合体育。找到你最感兴趣的体育项目，获取最新的赛事资讯和专业分析。',
    keywords: 'kaiyuntiyu分类,kaiyuntiyu体育,kaiyuntiyu足球,kaiyuntiyu篮球,kaiyuntiyu电竞',
    canonicalUrl: `${siteDomain}/category`,
    schema: [
      getWebPageSchema('全部分类 - kaiyuntiyu', '浏览全部体育分类', `${siteDomain}/category`),
      getBreadcrumbSchema([
        { name: '首页', url: siteDomain },
        { name: '全部分类', url: `${siteDomain}/category` },
      ]),
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-16">
        <Breadcrumb items={[
          { name: '首页', path: '/' },
          { name: '全部分类' },
        ]} />
      </div>

      <main>
        {/* Header */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-3">
              全部分类
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              五大体育分类，覆盖全球主流赛事，点击进入你感兴趣的体育世界
            </p>
          </div>
        </section>

        {/* Category Grid */}
        <section className="w-full pb-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-100 rounded-xl p-6 border border-background-200/70 hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 flex items-center justify-center bg-background-50 rounded-xl border border-background-200/70 group-hover:bg-primary-100/70 group-hover:border-primary-200 transition-colors flex-shrink-0">
                      <i className={`${cat.icon} text-foreground-600 group-hover:text-primary-600 text-2xl transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-lg font-semibold text-foreground-900 font-heading">
                          {cat.name}
                        </h2>
                        <span className="text-xs text-foreground-500 bg-background-200/70 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {cat.articleCount}篇
                        </span>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                        {cat.description}
                      </p>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 whitespace-nowrap">
                        查看分类 <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Across All Categories */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-10">
              最新资讯速览
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => {
                const category = categories.find((c) => c.slug === article.category);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                          {category?.name}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors font-heading">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{article.author}</span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}